import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { LayoutDashboard, RefreshCw } from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import { Header } from '@/components/layout/Header';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { getMyOverview } from '@/lib/api';
import { HubTiles } from '@/components/hub/HubTiles';
import { HubActionList } from '@/components/hub/HubActionList';
import { HubQuickActions, HubRecent } from '@/components/hub/HubWidgets';
import { HubActivityFeed } from '@/components/hub/HubActivityFeed';

export default function HubPage() {
  const { t } = useTranslation();
  const { user, isSiteManager } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  const load = () => {
    setLoading(true);
    getMyOverview()
      .then((r) => { setData(r.data); setError(false); })
      .catch(() => setError(true))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const firstName = (user?.name || '').split(' ')[0];

  return (
    <div className="min-h-screen bg-background" data-testid="hub-page">
      <Header />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
          <div>
            <p className="text-xs uppercase tracking-[0.2em] text-orange-500 font-bold mb-1">{t('hub.title')}</p>
            <h1 className="font-manrope text-3xl sm:text-4xl font-bold tracking-tight" data-testid="hub-title">
              {t('hub.greeting', { name: firstName })}
            </h1>
            <p className="text-muted-foreground mt-1">{t('hub.subtitle')}</p>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="ghost" size="sm" onClick={load} disabled={loading} data-testid="hub-refresh">
              <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
            </Button>
            {isSiteManager && (
              <Button variant="outline" size="sm" asChild data-testid="hub-dashboard-link">
                <Link to="/dashboard" className="gap-2"><LayoutDashboard className="h-4 w-4" /> {t('nav.dashboard')}</Link>
              </Button>
            )}
          </div>
        </div>

        {error && <p className="text-sm text-red-500 mb-4" data-testid="hub-error">{t('hub.loadError')}</p>}

        {loading && !data ? (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
            {[...Array(8)].map((_, i) => <Skeleton key={i} className="h-28 rounded-xl" />)}
          </div>
        ) : data && (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div className="lg:col-span-2 space-y-6">
              <HubTiles counts={data.counts} />
              <HubActivityFeed />
            </div>
            <div className="space-y-6">
              <HubActionList items={data.action_items || []} />
              <HubQuickActions user={user} />
              <HubRecent items={data.recent_transactions || []} />
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
